import { useState, useEffect, useRef, useMemo } from 'react';
import { IconType } from 'components/Icon';
import Icon from 'components/Icon';
import { DS_TAB_CHROME } from 'designSystem/util';
import { useTheme } from 'styled-components';
import { useSelector } from 'react-redux';
import { RootReducer } from 'redux/reducers';
import useDsNavigation from 'hooks/useDsNavigation';
import { searchDesignSystem, SearchResult } from './searchUtils';
import * as S from './styles';

interface SearchProps {
	compactMode?: 'full' | 'iconOnly' | 'hidden';
}

const Search = ({ compactMode = 'full' }: SearchProps) => {
	const { openTab } = useDsNavigation();
	const designSystemContent = useSelector((r: RootReducer) => r.mode.designSystemContent);
	const theme = useTheme();
	const [query, setQuery] = useState('');
	const [isOpen, setIsOpen] = useState(false);
	const [highlightedIndex, setHighlightedIndex] = useState(0);
	const containerRef = useRef<HTMLDivElement>(null);
	const inputRef = useRef<HTMLInputElement>(null);
	const isIconOnly = compactMode === 'iconOnly';
	const isHidden = compactMode === 'hidden';

	// Results are already sorted by relevance, then section
	const results = useMemo(() => {
		if (!designSystemContent || !query.trim()) return [];
		return searchDesignSystem(designSystemContent, query);
	}, [designSystemContent, query]);

	// Reset highlighted index when results change
	useEffect(() => {
		setHighlightedIndex(0);
	}, [results]);

	useEffect(() => {
		if (isOpen && isIconOnly) {
			const id = window.setTimeout(() => {
				inputRef.current?.focus();
			}, 0);
			return () => window.clearTimeout(id);
		}
	}, [isOpen, isIconOnly]);

	// Cmd+K / Ctrl+K to focus search
	useEffect(() => {
		const onKeyDown = (e: KeyboardEvent) => {
			if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
				e.preventDefault();
				if (isIconOnly) {
					setIsOpen(true);
				}
				requestAnimationFrame(() => inputRef.current?.focus());
			}
		};
		window.addEventListener('keydown', onKeyDown);
		return () => window.removeEventListener('keydown', onKeyDown);
	}, [isIconOnly]);

	// Escape to deactivate search (close dropdown, blur input)
	useEffect(() => {
		const onKeyDown = (e: KeyboardEvent) => {
			if (e.key !== 'Escape') return;
			if (!isOpen && document.activeElement !== inputRef.current) return;
			e.preventDefault();
			setIsOpen(false);
			inputRef.current?.blur();
		};

		window.addEventListener('keydown', onKeyDown);
		return () => window.removeEventListener('keydown', onKeyDown);
	}, [isOpen]);

	// Handle keyboard navigation (arrows, Enter)
	useEffect(() => {
		const onKeyDown = (e: KeyboardEvent) => {
			if (!isOpen || results.length === 0) return;
			if (document.activeElement !== inputRef.current) return;

			switch (e.key) {
				case 'ArrowDown':
					e.preventDefault();
					setHighlightedIndex((prev) => (prev + 1) % results.length);
					break;
				case 'ArrowUp':
					e.preventDefault();
					setHighlightedIndex((prev) => (prev - 1 + results.length) % results.length);
					break;
				case 'Enter':
					e.preventDefault();
					if (results[highlightedIndex]) {
						const result = results[highlightedIndex];
						openTab(result.tabKind, result.contentPath);
						setQuery('');
						setIsOpen(false);
					}
					break;
			}
		};

		window.addEventListener('keydown', onKeyDown);
		return () => window.removeEventListener('keydown', onKeyDown);
	}, [isOpen, results, highlightedIndex, openTab]);

	// Close dropdown when clicking outside
	useEffect(() => {
		const handleClickOutside = (e: MouseEvent) => {
			if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
				setIsOpen(false);
			}
		};

		if (isOpen) {
			document.addEventListener('mousedown', handleClickOutside);
			return () => document.removeEventListener('mousedown', handleClickOutside);
		}
	}, [isOpen]);

	const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value;
		setQuery(value);
		setIsOpen(isIconOnly ? true : value.trim().length > 0);
	};

	const onInputFocus = () => {
		if (isIconOnly || query.trim().length > 0) {
			setIsOpen(true);
		}
	};

	const onClear = () => {
		setQuery('');
		setIsOpen(false);
		inputRef.current?.focus();
	};

	const onIconClick = () => {
		setIsOpen((prev) => !prev);
	};

	const onResultClick = (result: SearchResult) => {
		openTab(result.tabKind, result.contentPath);
		setQuery('');
		setIsOpen(false);
	};

	// Calculate dropdown position to align with browser edge
	const getDropdownStyle = (): React.CSSProperties => {
		if (!containerRef.current) return {};

		const rect = containerRef.current.getBoundingClientRect();
		return {
			position: 'fixed',
			top: `${rect.bottom + 4}px`,
			right: '8px',
			left: 'auto',
		};
	};

	if (isHidden) return null;

	return (
		<S.Container ref={containerRef}>
			{isIconOnly ? (
				<S.IconButton onClick={onIconClick} aria-label="Search">
					<Icon type={IconType.SEARCH} width={16} height={16} />
				</S.IconButton>
			) : (
				<S.InputContainer>
					<S.SearchIcon>
						<Icon type={IconType.SEARCH} width={16} height={16} />
					</S.SearchIcon>
					<S.Input
						ref={inputRef}
						type="text"
						placeholder="Search DS..."
						value={query}
						onChange={onInputChange}
						onFocus={onInputFocus}
					/>
					{query && (
						<S.ClearButton onClick={onClear} aria-label="Clear search">
							<Icon type={IconType.CLOSE} width={14} height={14} />
						</S.ClearButton>
					)}
				</S.InputContainer>
			)}
			{isOpen && (
				<S.Dropdown $iconOnly={isIconOnly} style={getDropdownStyle()}>
					{isIconOnly && (
						<S.DropdownSearch>
							<S.InputContainer $fullWidth>
								<S.SearchIcon>
									<Icon type={IconType.SEARCH} width={16} height={16} />
								</S.SearchIcon>
								<S.Input
									ref={inputRef}
									type="text"
									placeholder="Search DS..."
									value={query}
									onChange={onInputChange}
									onFocus={onInputFocus}
								/>
								{query && (
									<S.ClearButton onClick={onClear} aria-label="Clear search">
										<Icon type={IconType.CLOSE} width={14} height={14} />
									</S.ClearButton>
								)}
							</S.InputContainer>
						</S.DropdownSearch>
					)}
					{results.length === 0 ? (
						<S.EmptyState>
							{query.trim().length === 0 ? 'Start typing to search' : 'No results found'}
						</S.EmptyState>
					) : (
						<S.ResultsList>
							{results.map((result, idx) => {
								const isHighlighted = idx === highlightedIndex;
								const chrome = DS_TAB_CHROME[result.tabKind];
								return (
									<S.ResultItem
										key={`${result.tabKind}-${result.contentPath || 'all'}-${idx}`}
										$highlighted={isHighlighted}
										onClick={() => onResultClick(result)}
									>
										<S.ResultHeader>
											<S.ResultLabel>{result.label}</S.ResultLabel>
											<S.SectionBadge>
												<Icon type={chrome.icon} width={10} height={10} color={theme.gray[600]} />
												<span>{chrome.label}</span>
											</S.SectionBadge>
										</S.ResultHeader>
										{result.matchContext && (
											<S.ResultContext>{result.matchContext}</S.ResultContext>
										)}
									</S.ResultItem>
								);
							})}
						</S.ResultsList>
					)}
				</S.Dropdown>
			)}
		</S.Container>
	);
};

export default Search;
